import { useRootStore } from 'store';
import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useApolloClient, ApolloClient, gql } from '@apollo/client';
import { FaFileImport, FaEye, FaFileDownload } from 'react-icons/fa';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { gqlAdd } from '../../graphql';

import { ImportProgressModal } from './ImportProgressModal';
import { useImportProgress } from './useImportProgress';
import { PreImportModal } from './PreImportModal';
import {
  dataFieldsForProcessing,
  getMappedFieldValue,
  ImportFieldMapState,
  mapHeaderToFields,
  resetRowFieldValues,
  resolveCollectionCode,
} from './import-column-mapper';
import { downloadCollectionImportTemplate } from './import-template';
import { getWorksheetRowValues, scanImportWorksheetRows } from './import-worksheet-scan';
import {
  annotateWorksheetWithResults,
  buildResultFileName,
  RowImportResult,
  workbookToResultBlob,
} from './import-result-annotator';
import {
  loadDeclaredOrdersForImport,
  matchDeclaredOrder,
  MatchableCollectionOrder,
} from './import-order-matcher';

// Component ----------------------------------------------
export const ImportExcel = ({ refetch, queryParams }) => {
  const client = useApolloClient();
  const { handleGraphqlError } = useHandleError('collections-import-xls');
  const fileInput = useRef<HTMLInputElement>(null);
  const { appStore } = useRootStore();
  const { t } = useTranslation();

  const [importState, importAPI] = useImportProgress(ImportExcel);
  const [showModal, setShowModal] = useState(false);
  const [showPreModal, setShowPreModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const openFileBrowser = () => {
    if (!fileInput.current) return;
    // Επαναφορά ώστε να πυροδοτείται το onChange και με το ίδιο αρχείο
    fileInput.current.value = '';
    fileInput.current.click();
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadCollectionImportTemplate();
    } catch (err) {
      console.error('Λήψη προτύπου εισαγωγής:', err);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleChange = async (e) => {
    if (!e.target.files.length) return;
    const file = e.target.files[0];
    setSelectedFile(file);
    setShowPreModal(true);
  };

  const handlePreImportSubmit = (values) => {
    setShowPreModal(false);
    setShowModal(true);
    if (selectedFile) {
      handleImport(selectedFile, values.collectorId);
    }
    setSelectedFile(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  async function handleImport(target: File, collectorId: number): Promise<void> {
    const Excel = await import('exceljs');
    const wb = new Excel.Workbook();
    const reader = new FileReader();

    reader.readAsArrayBuffer(target);

    reader.onload = () => {
      importAPI.start();
      importAPI.advanceTo('load');
      importAPI.log('info', `Έναρξη εισαγωγής (Συλλέκτης ID: ${collectorId})`);

      const buffer = reader.result as ArrayBuffer;
      importAPI.advanceTo('parse');

      wb.xlsx.load(buffer).then(async (workbook) => {
        for (let index = 1; index < workbook._worksheets.length; index++) {
          const sheet = workbook._worksheets[index];
          if (index > 1) break;

          importAPI.setWorksheetName(sheet?.name);
          importAPI.setStep('load', 'done');
          importAPI.setStep('parse', 'done');
          importAPI.advanceTo('header');
          importAPI.log('info', `Επεξεργασία φύλλου: ${sheet?.name || 'Άγνωστο'}`);

          importAPI.advanceTo('header');
          const headerRowIdx = findHeaderRow(sheet);
          if (headerRowIdx == undefined) {
            const errorMsg = `Δεν βρέθηκε επικεφαλίδα στο φύλλο ${sheet.name}`;
            importAPI.log('error', errorMsg);
            importAPI.setStep('header', 'error');
            importAPI.incError();
            importAPI.stop();
            continue;
          }
          importAPI.log('info', `Βρέθηκε επικεφαλίδα στη γραμμή ${headerRowIdx + 1}`);

          importAPI.advanceTo('map');
          let fieldMapState: ImportFieldMapState;
          try {
            const headerRow = sheet._rows[headerRowIdx]._cells.map((v) => v._value.value);
            fieldMapState = mapHeaderToFields(headerRow);
            const strategyLabel =
              fieldMapState.codeStrategy === 'single' ? 'μία στήλη κωδικού' : 'δύο στήλες κωδικού';
            importAPI.log('info', `Επιτυχής αντιστοίχιση στηλών (${strategyLabel})`);
          } catch (error) {
            const errorMsg = `Δεν βρέθηκαν όλα τα πεδία στην επικεφαλίδα του φύλλου "${
              sheet.name
            }" \n${(error as Error).message}`;
            importAPI.log('error', errorMsg);
            importAPI.setStep('map', 'error');
            importAPI.incError();
            importAPI.stop();
            continue;
          }

          importAPI.advanceTo('iterate');
          const rowScan = scanImportWorksheetRows(sheet, headerRowIdx, fieldMapState);
          let nullRowCount = 0;
          let rowsHandled = 0;
          /** Αποτελέσματα ανά γραμμή για annotation στο Excel */
          const rowResults: RowImportResult[] = [];

          // Αιτήσεις δηλωμένες από οδηγό — για σύνδεση με συλλογή βάσει ΔΑ + σημείου
          let declaredOrders: MatchableCollectionOrder[] = [];
          try {
            declaredOrders = await loadDeclaredOrdersForImport(client, collectorId);
            importAPI.log(
              'info',
              `Φορτώθηκαν ${declaredOrders.length} αιτήσεις σε κατάσταση «Παραληφθείσα από Οδηγό» για αντιστοίχιση.`,
            );
          } catch (loadOrdersErr) {
            importAPI.log(
              'warn',
              `Δεν φορτώθηκαν αιτήσεις για αντιστοίχιση: ${(loadOrdersErr as Error).message}`,
            );
          }

          importAPI.log(
            'info',
            `Ξεκινάει επεξεργασία ${rowScan.totalRows} γραμμών δεδομένων (γραμμές ${
              rowScan.startRow + 1
            }–${rowScan.endRowInclusive + 1})`,
          );

          for (let j = rowScan.startRow; j <= rowScan.endRowInclusive; j++) {
            rowsHandled++;
            importAPI.setProgress((rowsHandled / rowScan.totalRows) * 100);

            const row = getWorksheetRowValues(sheet, j);
            if (!row) continue;

            if (nullRowCount >= 2) {
              importAPI.log('info', 'Τερματισμός λόγω 2 συνεχόμενων κενών γραμμών');
              importAPI.setTerminatedByEmptyRows(j);
              break;
            }

            importAPI.advanceTo('validate');
            resetRowFieldValues(fieldMapState);

            const code = resolveCollectionCode(fieldMapState, row);
            if (code === null) {
              nullRowCount += 1;
              importAPI.log('info', `Κενή γραμμή (row=${j}) — skip`, j);
              continue;
            }
            nullRowCount = 0;

            let skipRow = false;
            let materialId: number | undefined;
            let rowErrorMessage: string | undefined;

            for (const mapped of dataFieldsForProcessing(fieldMapState)) {
              if (skipRow) break;

              const value = row[mapped.colIdx];
              mapped.colValue = value;
              const fieldName = mapped.def.field;

              if (fieldName === 'collectionPointId') {
                const collectionpointId = await getCollectionpointByLegacyAppId(client, value);
                if (!collectionpointId) {
                  const msg = `Δεν βρέθηκε το Σημείο Συλλογής με κωδικό ERP="${value}".`;
                  rowErrorMessage = msg;
                  importAPI.log('error', `${msg} (row=${j})`, j);
                  importAPI.incError();
                  importAPI.setStep('validate', 'error');
                  skipRow = true;
                  continue;
                }
                mapped.fieldValue = collectionpointId;
                importAPI.log(
                  'info',
                  `Βρέθηκε το Σημείο Συλλογής με id="${collectionpointId}".`,
                  j,
                );
              } else if (fieldName === 'wastereceptionpointId') {
                // Επίλυση Σημείου Παραλαβής από ERP κωδικό της γραμμής Excel
                const wrpId = await getWastereceptionpointByErpId(client, value, collectorId);
                if (!wrpId) {
                  const msg = `Δεν βρέθηκε το Σημείο Παραλαβής με ERP κωδικό="${value}" για τον συλλέκτη.`;
                  rowErrorMessage = msg;
                  importAPI.log('error', `${msg} (row=${j})`, j);
                  importAPI.incError();
                  importAPI.setStep('validate', 'error');
                  skipRow = true;
                  continue;
                }
                mapped.fieldValue = wrpId;
                importAPI.log('info', `Βρέθηκε το Σημείο Παραλαβής με id="${wrpId}".`, j);
              } else if (fieldName === 'jobtypeId') {
                const job = await getJobtypeByCode(client, value);
                if (!job) {
                  const msg = `Δεν βρέθηκε η Εργασία / Απόβλητο με τιμή "${value}".`;
                  rowErrorMessage = msg;
                  importAPI.log('error', `${msg} (row=${j})`, j);
                  importAPI.incError();
                  importAPI.setStep('validate', 'error');
                  skipRow = true;
                  continue;
                }
                mapped.fieldValue = job.id;
                importAPI.log('info', `Βρέθηκε η Εργασία / Απόβλητο "${job.id}".`, j);
                materialId = job.materialId;
              } else {
                mapped.fieldValue = value;
              }
            }

            if (skipRow) {
              rowResults.push({
                sheetRowIndex: j,
                status: 'error',
                message: rowErrorMessage || 'Σφάλμα επικύρωσης',
              });
              continue;
            }

            const collectionPointId = getMappedFieldValue(
              fieldMapState,
              'collectionPointId',
            ) as number;

            // Αντιστοίχιση αίτησης: ίδιο ΔΑ (χωρίς κενά) + ίδιο σημείο συλλογής
            const orderMatch = matchDeclaredOrder(declaredOrders, code, collectionPointId);
            if (orderMatch.kind === 'mismatch') {
              importAPI.log('error', `${orderMatch.reason} (row=${j})`, j);
              importAPI.incError();
              importAPI.setStep('validate', 'error');
              rowResults.push({
                sheetRowIndex: j,
                status: 'error',
                collectionOrderId: orderMatch.order.id,
                collectionOrderCode: orderMatch.order.code ?? undefined,
                message: orderMatch.reason,
              });
              continue;
            }

            let matchedOrder: MatchableCollectionOrder | undefined;
            if (orderMatch.kind === 'linked') {
              matchedOrder = orderMatch.order;
              importAPI.log(
                'info',
                `Αντιστοιχίστηκε αίτηση ${matchedOrder.code ?? matchedOrder.id} (ΔΑ χωρίς κενά).`,
                j,
              );
            }

            importAPI.advanceTo('check');
            const where = {
              collectorId,
              legacyAppId: code,
            };
            const exists = await client.query({
              query: gql`
                query getCollections($limit: Int, $offset: Int, $order: String, $where: String) {
                  getCollections(limit: $limit, offset: $offset, order: $order, where: $where) {
                    recordCount
                    data {
                      id
                    }
                  }
                }
              `,
              variables: { where: JSON.stringify(where) },
            });
            if (exists.data.getCollections.recordCount > 0) {
              const existingId = exists.data.getCollections.data?.[0]?.id as number | undefined;
              importAPI.log('warn', `Διπλότυπο: Η εγγραφή υπάρχει ήδη "${code}" (row=${j})`, j);
              importAPI.incDuplicate();
              importAPI.incProcessed();
              rowResults.push({
                sheetRowIndex: j,
                status: 'duplicate',
                collectionId: existingId,
                message: `Η εγγραφή υπάρχει ήδη (κωδικός "${code}")`,
              });
              continue;
            }

            importAPI.advanceTo('insert');
            const collection: any = {
              code,
              legacyAppId: code,
              collectorId,
              wastereceptionpointId: getMappedFieldValue(fieldMapState, 'wastereceptionpointId'),
              collectionPointId,
              dateCollected: getMappedFieldValue(fieldMapState, 'dateCollected'),
              finDispNote: code,
              ...(matchedOrder ? { collectionOrderId: matchedOrder.id } : {}),
              collectionLines: [
                {
                  aa: 1,
                  materialId,
                  jobtypeId: getMappedFieldValue(fieldMapState, 'jobtypeId'),
                  qty: getMappedFieldValue(fieldMapState, 'qty'),
                  netWeight: getMappedFieldValue(fieldMapState, 'netWeight'),
                  finDispNote: code,
                },
              ],
            };

            try {
              const mutationResult = await client.mutate({
                mutation: gqlAdd,
                variables: { data: collection },
              });
              const newId = mutationResult?.data?.addCollection?.id as number | undefined;

              // Αφαίρεση από τοπική λίστα ώστε να μην ξανασυνδεθεί σε άλλη γραμμή
              if (matchedOrder) {
                declaredOrders = declaredOrders.filter((o) => o.id !== matchedOrder!.id);
              }

              const linkMsg = matchedOrder
                ? ` · αίτηση ${matchedOrder.code ?? matchedOrder.id}`
                : '';
              importAPI.log(
                'info',
                `Επιτυχής καταχώριση: ${code}${newId != null ? ` (ID=${newId})` : ''}${linkMsg}`,
                j,
              );
              importAPI.incInserted();
              importAPI.incProcessed();
              rowResults.push({
                sheetRowIndex: j,
                status: 'success',
                collectionId: newId,
                collectionOrderId: matchedOrder?.id,
                collectionOrderCode: matchedOrder?.code ?? undefined,
                message:
                  newId != null
                    ? `Καταχωρήθηκε με ID ${newId}`
                    : 'Καταχωρήθηκε επιτυχώς',
              });
            } catch (error) {
              const msg = `Σφάλμα καταχώρισης: ${(error as Error).message}`;
              importAPI.log('error', `${msg} (row=${j})`, j);
              importAPI.incError();
              importAPI.setStep('insert', 'error');
              rowResults.push({
                sheetRowIndex: j,
                status: 'error',
                message: msg,
              });
            }
          }

          // Ενημέρωση Excel με στήλες αποτελέσματος και προετοιμασία λήψης
          if (rowResults.length > 0) {
            try {
              annotateWorksheetWithResults(sheet, headerRowIdx, rowResults);
              const resultBlob = await workbookToResultBlob(workbook);
              const resultFileName = buildResultFileName(target.name);
              importAPI.setResultFile({ blob: resultBlob, fileName: resultFileName });
              importAPI.log(
                'info',
                `Έτοιμο αρχείο αποτελεσμάτων (${rowResults.length} γραμμές με κατάσταση).`,
              );
            } catch (annotateErr) {
              importAPI.log(
                'warn',
                `Δεν ήταν δυνατή η δημιουργία αρχείου αποτελεσμάτων: ${
                  (annotateErr as Error).message
                }`,
              );
            }
          }

          importAPI.setProgress(100);
          importAPI.setStep('insert', 'done');
          importAPI.stop();

          const s = importAPI.getState();
          const reasons: string[] = [];
          if (s.terminatedByEmptyRows) {
            reasons.push(
              `Τερματισμός λόγω 2 συνεχόμενων κενών γραμμών (στη γραμμή ${s.terminatedByEmptyRows.atRow}).`,
            );
          }
          if (s.inserted === 0 && s.duplicates > 0) {
            reasons.push('Όλες οι εγγραφές ήταν ήδη καταχωρημένες (διπλότυπα).');
          }
          if (s.inserted === 0 && s.processed === 0 && s.errors > 0) {
            reasons.push(
              'Καμία έγκυρη γραμμή (π.χ. δεν βρέθηκαν αναφορές σε Σημείο Συλλογής/Παραλαβής/Εργασία).',
            );
          }
          if (
            s.inserted === 0 &&
            s.processed > 0 &&
            s.errors > 0 &&
            s.duplicates === 0 &&
            !s.terminatedByEmptyRows
          ) {
            reasons.push('Υπήρξαν σφάλματα και δεν καταχωρήθηκε καμία εγγραφή.');
          }

          const status =
            s.inserted === 0 ? 'empty' : s.errors > 0 || s.duplicates > 0 ? 'partial' : 'success';

          importAPI.setOutcome({ status, reasons });
          importAPI.log(
            'info',
            `Ολοκληρώθηκε η εισαγωγή. Επεξεργάστηκαν: ${s.processed}, Καταχωρήθηκαν: ${s.inserted}, Διπλότυπα: ${s.duplicates}, Σφάλματα: ${s.errors}`,
            undefined,
            { stepKey: 'insert' },
          );
        }
      });
    };
  }

  if (!(appStore.isPlatformOwner || appStore.isCollector)) return <></>;
  return (
    <>
      <span className="d-inline-flex align-items-center">
        <Button
          variant="outline-secondary"
          size="sm"
          className="text-nowrap me-2"
          style={{ zIndex: 0 }}
          onClick={handleDownloadTemplate}
          disabled={importState.running || downloadingTemplate}
          title={t('Λήψη προτύπου Excel')}
        >
          <FaFileDownload className="mb-1 me-1 flex-shrink-0" />
          {downloadingTemplate ? t('Λήψη…') : t('Πρότυπο Εισαγωγής')}
        </Button>

        <Button
          variant="info"
          size="sm"
          className="text-nowrap"
          style={{ minWidth: '185px', zIndex: 0 }}
          onClick={openFileBrowser}
          disabled={importState.running}
        >
          <FaFileImport className="mb-1 me-2 flex-shrink-0" />
          {t('Εισαγωγή από Excel')}
        </Button>

        {(importState.running || importState.logs.length > 0) && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="ms-2 d-inline-flex align-items-center shadow-sm"
            style={{ zIndex: 0 }}
            onClick={() => setShowModal(true)}
            title="Προβολή προόδου εισαγωγής"
          >
            <FaEye className="me-1" /> {importState.running ? 'Σε εξέλιξη…' : 'Προβολή Εισαγωγής'}
          </Button>
        )}

        <input hidden type="file" onChange={handleChange} accept=".xlsx" ref={fileInput} />
      </span>

      <PreImportModal
        show={showPreModal}
        onClose={() => {
          setShowPreModal(false);
          setSelectedFile(null);
          if (fileInput.current) fileInput.current.value = '';
        }}
        onSubmit={handlePreImportSubmit}
        onDownloadTemplate={handleDownloadTemplate}
      />

      <ImportProgressModal
        show={showModal}
        onClose={() => setShowModal(false)}
        state={importState}
      />
    </>
  );
};

function findHeaderRow(sheet) {
  let headerRowIdx: number = undefined;
  for (let j = 0; j < sheet._rows.length; j++) {
    if (sheet._rows[j] !== undefined) {
      headerRowIdx = j;
      break;
    }
  }
  return headerRowIdx;
}

async function getCollectionpointByLegacyAppId(client: ApolloClient<object>, value: any) {
  const gqlScript = gql`
    query getQuery($where: String) {
      getCollectionpoints(where: $where) {
        data {
          id
          name
        }
      }
    }
  `;
  const where = {
    legacyAppId: value,
  };
  const ret = await client.query({
    query: gqlScript,
    variables: { where: JSON.stringify(where) },
    fetchPolicy: 'network-only',
  });

  const data = ret?.data?.getCollectionpoints?.data;
  return Array.isArray(data) && data.length ? data[0].id : undefined;
}

async function getJobtypeByCode(
  client: ApolloClient<object>,
  value: any,
): Promise<{ id: number; materialId: number } | undefined> {
  const gqlScript = gql`
    query getQuery($where: String) {
      getJobtypes(where: $where) {
        data {
          id
          materialId
        }
      }
    }
  `;
  const where = {
    code: value,
  };
  const ret = await client.query({
    query: gqlScript,
    variables: { where: JSON.stringify(where) },
    fetchPolicy: 'cache-first',
  });

  const data = ret?.data?.getJobtypes?.data;
  return Array.isArray(data) && data.length ? data[0] : undefined;
}

/** Αναζήτηση Σημείου Παραλαβής με ERP κωδικό, περιορισμένο στον συλλέκτη της εισαγωγής */
async function getWastereceptionpointByErpId(
  client: ApolloClient<object>,
  erpId: any,
  collectorId: number,
): Promise<number | undefined> {
  if (erpId == null || String(erpId).trim() === '') return undefined;

  const gqlScript = gql`
    query getQuery($where: String) {
      getWastereceptionpoints(where: $where) {
        data {
          id
          name
        }
      }
    }
  `;
  const where = {
    erpId: String(erpId).trim(),
    collectorId,
  };
  const ret = await client.query({
    query: gqlScript,
    variables: { where: JSON.stringify(where) },
    fetchPolicy: 'network-only',
  });

  const data = ret?.data?.getWastereceptionpoints?.data;
  return Array.isArray(data) && data.length ? data[0].id : undefined;
}
